/**
 * Reference Field Utilities
 * Handles resolving references to types, tags, and other items
 */

import { applyTextCase } from './processing';

/**
 * Lookup context for resolving reference fields
 */
export interface LookupContext {
  types?: any[];
  tags?: any[];
  allItems?: any[];
  dialogs?: any[];
  documents?: any[];
}

/**
 * Result of field export mapping, including both the display value and resolved reference data
 */
export interface FieldExportResult {
  value: any;           // The value to display (e.g., extracted name)
  referenceData?: any;  // The full resolved reference object(s)
}

/**
 * Resolve a reference ID to an actual object from the lookup context
 */
export function resolveReferenceById(
  refId: string,
  fieldType: string,
  lookupContext?: LookupContext
): any | null {
  if (!lookupContext || !refId) return null;

  if (fieldType === 'types' && lookupContext.types) {
    // Types store contains TypeItem objects, each with a data array of TypeElement objects
    for (const typeItem of lookupContext.types) {
      if (typeItem.data && Array.isArray(typeItem.data)) {
        const element = typeItem.data.find((el: any) => el.id === refId);
        if (element) {
          return { ...element, _parentTypeName: typeItem.name };
        }
      }
    }
  } else if (fieldType === 'tags' && lookupContext.tags) {
    // Similar structure for tags
    for (const tagItem of lookupContext.tags) {
      if (tagItem.data && Array.isArray(tagItem.data)) {
        const element = tagItem.data.find((el: any) => el.id === refId);
        if (element) return element;
      }
    }
  } else if (fieldType === 'glossaryItem' && lookupContext.allItems) {
    return lookupContext.allItems.find((item: any) => item.id === refId) || null;
  } else if (fieldType === 'dialog' && lookupContext.dialogs) {
    // Dialogs can be referenced by id or dialog_key
    return lookupContext.dialogs.find((dialog: any) => 
      dialog.id === refId || dialog.dialog_key === refId
    ) || null;
  } else if (fieldType === 'document' && lookupContext.documents) {
    // Documents are referenced by id
    return lookupContext.documents.find((doc: any) => doc.id === refId) || null;
  }

  return null;
}

/**
 * Apply field export mapping to a field value
 * Extracts the configured property from reference fields (glossaryItem, types, etc.)
 * Returns both the display value and the resolved reference data
 * 
 * @param fieldValue - The raw field value
 * @param fieldType - The field type (e.g., 'types', 'glossaryItem')
 * @param fieldId - The field ID for per-field mapping lookup
 * @param categoryFieldMappings - Per-category, per-field mappings (fieldId -> export property)
 * @param categoryName - The category name to look up field mappings
 * @param fieldExportMappings - Global type-based mappings (fieldType -> export property)
 * @param lookupContext - Context for resolving reference IDs
 */
export function applyFieldExportMapping(
  fieldValue: any,
  fieldType: string,
  fieldId?: string,
  categoryFieldMappings?: Record<string, Record<string, string>>,
  categoryName?: string,
  fieldExportMappings?: Record<string, string>,
  lookupContext?: LookupContext
): FieldExportResult {
  // If value is empty, return as-is
  if (!fieldValue) {
    return { value: fieldValue, referenceData: undefined };
  }

  // Convert comma-separated string to array for reference fields
  if (typeof fieldValue === 'string' && fieldValue.includes(',') &&
    (fieldType === 'glossaryItem' || fieldType === 'types' || fieldType === 'tags' || fieldType === 'dialog' || fieldType === 'document')) {
    fieldValue = fieldValue.split(',').map(id => id.trim()).filter(id => id.length > 0);
    console.log(`🔄 Converted comma-separated string to array for ${fieldType}:`, fieldValue);
  }

  // First check per-field mapping (categoryFieldMappings[categoryName][fieldId])
  let exportBehavior: string | undefined;
  if (categoryFieldMappings && categoryName && fieldId) {
    const categoryMappings = categoryFieldMappings[categoryName];
    if (categoryMappings && categoryMappings[fieldId]) {
      exportBehavior = categoryMappings[fieldId];
    }
  }

  // Fall back to type-based mapping
  if (!exportBehavior && fieldExportMappings) {
    exportBehavior = fieldExportMappings[fieldType];
  }

  // Parse property and text case from export behavior (e.g., 'name:uppercase')
  let exportProperty: string | undefined;
  let textCase: 'uppercase' | 'lowercase' | 'original' = 'original';

  if (exportBehavior) {
    if (exportBehavior.includes(':')) {
      const [prop, caseStr] = exportBehavior.split(':');
      exportProperty = prop;
      if (caseStr === 'uppercase' || caseStr === 'lowercase') {
        textCase = caseStr;
      }
    } else {
      exportProperty = exportBehavior;
    }
  }

  // Helper to resolve a single value and return both display value and resolved object
  const resolveValue = (val: any): { displayValue: any; resolvedObject: any } => {
    let displayValue: any;
    let resolvedObject: any = undefined;

    // First, extract text from TranslatableValue format if present
    let valueToProcess = val;
    if (val && typeof val === 'object' && 'text' in val) {
      // TranslatableValue format - extract the text
      valueToProcess = val.text;
    }

    // If it's a string (likely an ID), try to look it up
    if (typeof valueToProcess === 'string' && lookupContext) {
      const resolved = resolveReferenceById(valueToProcess, fieldType, lookupContext);
      if (resolved) {
        resolvedObject = resolved; // Store the full resolved object
        if (exportProperty) {
          displayValue = resolved[exportProperty] || resolved.name || resolved.id || valueToProcess;
        } else {
          displayValue = resolved.name || resolved.id || valueToProcess;
        }
      } else {
        // If not resolved and no export property, return the string as-is
        displayValue = valueToProcess;
      }
    }
    // If it's an object, extract the property
    else if (typeof valueToProcess === 'object' && valueToProcess !== null) {
      resolvedObject = valueToProcess; // Store the full object
      if (exportProperty) {
        displayValue = valueToProcess[exportProperty] || valueToProcess.name || valueToProcess.id || valueToProcess.url || valueToProcess;
      } else {
        displayValue = valueToProcess.name || valueToProcess.id || valueToProcess;
      }
    } else {
      displayValue = valueToProcess;
    }

    // Apply text case transformation for reference fields
    if (typeof displayValue === 'string' && textCase !== 'original') {
      displayValue = applyTextCase(displayValue, textCase);
    }

    return { displayValue, resolvedObject };
  };

  // Handle array values (multiple references)
  if (Array.isArray(fieldValue)) {
    const results = fieldValue.map(resolveValue);
    return {
      value: results.map(r => r.displayValue),
      referenceData: results.map(r => r.resolvedObject).filter(obj => obj !== undefined)
    };
  }

  // Handle single value
  const result = resolveValue(fieldValue);
  return {
    value: result.displayValue,
    referenceData: result.resolvedObject
  };
}
